import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    // NL WS3: remember the colour this gallery shows, so a size pick never swaps the media.
    this.#colour = MediaGallery.colourOf(target?.querySelector('variant-picker'));

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** @type {string | null} */
  #colour = null;

  /**
   * NL WS3: the selected colour value in a variant picker ('' when the product has no colour option).
   * @param {Element | null | undefined} picker
   * @returns {string | null} null when there is no picker to read.
   */
  static colourOf(picker) {
    if (!picker) return null;
    const isColour = (/** @type {string | undefined} */ name) => /^\s*colou?r\s*$/i.test(name || '');
    for (const select of picker.querySelectorAll('select')) {
      const opt = select.selectedOptions[0];
      if (opt && isColour(opt.dataset.optionName)) return opt.value;
    }
    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (picker.querySelectorAll('fieldset input:checked'))) {
      if (isColour(input.dataset.optionName)) return input.value;
    }
    return '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // NL WS3: only a colour change (or a different product) may swap the media. A size pick keeps the
    // gallery as it is: same image nodes, same slide, no new image requests, no flash.
    const picker = event.target.closest('variant-picker') ?? event.target;
    const nextColour = MediaGallery.colourOf(picker);
    const connectedUrl = event.detail?.connectedProductUrl;
    const currentUrl = picker instanceof HTMLElement ? picker.dataset.productUrl?.split('?')[0] : undefined;
    const newProduct = !!connectedUrl && !!currentUrl && connectedUrl !== currentUrl;
    if (!newProduct && nextColour !== null && this.#colour !== null && nextColour === this.#colour) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
